import { useSearch } from "@tanstack/react-router";
import { Bar, CartesianGrid, ComposedChart, LabelList, Line, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import type { Page } from "../../api";
import { TabPage } from "../../PageFrame";
import { Legend, Swatch } from "../bars";
import { axisHeight, axisTicks, AxisTick, CHART, chartWidth, ScrollChart, TOOLTIP_STYLE } from "../charts";
import type { PageProps } from "../index";
import { monthGB, PageHead } from "../kit";
import { ALL_ROWS, pageExtra, pageFilter } from "../params";
import { VariantPage } from "../variant";

export interface MonthRow {
  month: string;
  offers_avg_gbp: number | null;
  offers_n: number;
  purchases_avg_gbp: number | null;
  purchases_n: number;
}

export interface MonthPoint {
  label: string;
  /** Null in a month with no purchase or no offer: the chart leaves it blank, never 0. */
  purchased: number | null;
  offer: number | null;
}

export function monthPoints(rows: MonthRow[]): MonthPoint[] {
  return rows.map((row) => ({ label: monthGB(row.month), purchased: row.purchases_avg_gbp, offer: row.offers_avg_gbp }));
}

export const hasMonthData = (rows: MonthRow[]) => rows.some((row) => row.purchases_avg_gbp != null || row.offers_avg_gbp != null);

/** The y axis runs from just under the lowest price to just over the highest, so small moves stay readable. */
export function monthDomain(points: MonthPoint[]): [number, number] {
  const values = points.flatMap((point) => [point.purchased, point.offer]).filter((value): value is number => value != null);
  if (values.length === 0) return [0, 1];
  return [Math.floor(Math.min(...values) - 1), Math.ceil(Math.max(...values) + 0.5)];
}

const two = (value: unknown) => (value == null ? "" : Number(value).toFixed(2));

/** Purchases as bars, offers as a line, one slot per month. */
export function MonthlyChart({ points, width, height }: { points: MonthPoint[]; width?: number; height?: number }) {
  const [bottom, top] = monthDomain(points);
  return (
    <ComposedChart data={points} width={width} height={height} margin={{ top: 22, right: 12, bottom: 0, left: 0 }}>
      <CartesianGrid vertical={false} stroke={CHART.grey4} />
      <XAxis
        dataKey="label"
        interval={0}
        height={axisHeight(true, points.map((point) => point.label))}
        tickLine={false}
        stroke={CHART.grey1}
        tick={(props: { x?: number | string; y?: number | string; index?: number; payload?: { value?: string | number } }) => (
          <AxisTick {...props} rotate links={[]} />
        )}
      />
      <YAxis
        width={44}
        tickLine={false}
        axisLine={false}
        stroke={CHART.grey1}
        tick={{ fontSize: 10 }}
        tickFormatter={(value: number) => value.toFixed(1)}
        domain={[bottom, top]}
        ticks={axisTicks(top, bottom)}
      />
      <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => two(value)} />
      <Bar dataKey="purchased" name="Purchased" fill={CHART.purple} isAnimationActive={false}>
        <LabelList dataKey="purchased" position="top" fontSize={10} fill={CHART.ink} formatter={two} />
      </Bar>
      <Line
        type="linear"
        dataKey="offer"
        name="Offer"
        stroke={CHART.purple1}
        strokeWidth={2.5}
        dot={{ r: 4, fill: CHART.purple1 }}
        connectNulls
        isAnimationActive={false}
      >
        <LabelList dataKey="offer" position="top" fontSize={10} fill={CHART.purple1} formatter={two} />
      </Line>
    </ComposedChart>
  );
}

export function MonthlyBody({ rows }: { rows: MonthRow[] }) {
  const points = monthPoints(rows);
  return (
    <>
      <Legend>
        <Swatch className="h-[3px] bg-ks-purple-1">Offer</Swatch>
        <Swatch className="bg-ks-purple">Purchased</Swatch>
      </Legend>
      <ScrollChart width={chartWidth(points.length, 44)} toEnd>
        <ChartFrame title="Offers and purchases by month" height={280} pointCount={points.length}>
          <MonthlyChart points={points} />
        </ChartFrame>
      </ScrollChart>
    </>
  );
}

export function MonthlyOffersPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const variant = pageFilter(search, "variant");
  return (
    <VariantPage tab={tab}>
      <TabPage<Page<MonthRow>>
        tab={tab}
        areaName={areaName}
        extra={{ ...pageExtra(tab.tabKey, search), limit: ALL_ROWS }}
        isEmpty={(data) => !hasMonthData(data.items)}
        head={(data) => (
          <PageHead title={tab.label} sub={data && variant ? `${variant} · GBP per kg · months with no data left blank` : undefined} />
        )}
      >
        {(data) => <MonthlyBody rows={data.items} />}
      </TabPage>
    </VariantPage>
  );
}
